import type { AIConfig } from "@evidenceb/ai-handler";
import type { AssetsDetails } from "@evidenceb/athena-common/modules/Assets";
import { featureFlagsStore } from "@evidenceb/athena-common/modules/FeatureFlags";
import {
  applyThemeWithLegacy,
  ThemeType,
} from "@evidenceb/athena-common/modules/Theme";
import type { Milliseconds } from "@evidenceb/athena-events-tools";
import {
  type LoadPhase,
  reportAppLoaded,
  startLoadPhase,
  trackLoadPhase,
} from "@evidenceb/debug-utils";
import type { Module, Objective } from "@evidenceb/gameplay-interfaces";
import * as Sentry from "@sentry/react";
import axios from "axios";
import merge from "lodash/merge";
import { useContext, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import appConfig from "../../config";
import { SERVICE_WORKER_FLAGS } from "../../const/flags";
import { configStore } from "../../contexts/ConfigContext";
import { contentPagesStore } from "../../contexts/ContentPagesContext";
import { dataStore } from "../../contexts/DataContext";
import { errorStore } from "../../contexts/ErrorContext";
import { useAthenaEventsRecentlySynced } from "../../contexts/EventStreamContext";
import { homeStore } from "../../contexts/HomeContext";
import { sessionStore } from "../../contexts/SessionContext";
import type { LearningSet } from "../../interfaces/AthenaResources";
import { AuthMode, type Config, Pagetype } from "../../interfaces/Config";
import type { ContentPage } from "../../interfaces/ContentPage";
import type { Data } from "../../interfaces/Data";
import type { Home } from "../../interfaces/Home";
import { StatementHistory } from "../../interfaces/Session";
import { LoaderStatus } from "../../interfaces/Status";
import {
  type Student,
  type Subject,
  type Teacher,
  type TokenPayload,
  type User,
  type UserExtra,
  UserType,
} from "../../interfaces/User";
import type {
  ExperimentationSet,
  Test,
} from "../../pages/Workmodes/Experimentation/type";
import {
  flattenPool,
  getExperimentationConfig,
  normalizeExperimentationConfig,
} from "../../pages/Workmodes/Experimentation/utils";
import {
  type AuthService,
  authMatch,
  getGoogleClassroomData,
  getLogoutRedirectUrl,
  isSameGoogleClassroomUser,
  redirectIfNotAuthPath,
  updateGoogleClassroomData,
} from "../../utils/auth";
import { axiosClient } from "../../utils/axios-client/axios-client";
import { getEnvName, getHost, isProductionEnvironment } from "../../utils/env";
import { getLocalStorageFlags, setLocalStorageFlag } from "../../utils/flags";
import { effectiveFragments } from "../../utils/fragments";
import {
  applicationResolver,
  flattenBooleansInObject,
  getSpecimenVariation,
  isSpecimenVersion,
  registerDebugUtils,
  removeNonVisible,
  shouldSignIn,
  tokenResolver,
  versionResolver,
} from "../../utils/init";
import * as localStorage from "../../utils/localStorage";
import { mappingTreeToSubjects } from "../../utils/mapping";
import { userDuoToSessionDuo, userToSession } from "../../utils/session";
import {
  clearStaleClientReloadGuard,
  reloadOnceWithFreshClient,
} from "../../utils/stale-client";
import useAthenaAPIClient from "../queries/useAthenaAPIClient/useAthenaAPIClient";
import { useExercisesQuery } from "../queries/useExercisesQuery";
import { PLACEHOLDER_SUBJECT } from "../useActiveSubject";
import useSelectApp from "../useSelectApp";
import { INIT_ERRORS, type InitError } from "./initErrors";
import useAxiosClientInit from "./useAxiosClientInit";
import { useEventStreamEarlySubscribers } from "./useEventStreamEarlySubscribers";
import useInitServiceWorker from "./useInitServiceWorker";
import useSetSentryContext from "./useSetSentryContext";

const REQUIRED_SYNC_RECENCY: Milliseconds = 4 * 60 * 60 * 1_000; // 4 hours
// In e2e/local dev contexts there's no real backend latency to wait out, so
// this can be much shorter than production's 10s — keeps e2e tests from
// paying the full production wait on every run.
const MAX_SYNC_WAITING_TIME: Milliseconds = import.meta.env.DEV
  ? 1 * 1_000 // 1 second
  : 10 * 1_000; // 10 seconds

const resolveSentryRelease = () => {
  if (import.meta.env.VITE_SENTRY_RELEASE)
    return import.meta.env.VITE_SENTRY_RELEASE;
  else return "UNKNOWN";
};

export interface GlobalConfig {
  // TEMP
  config: Omit<Config, "ai"> & { ai: AIConfig };
  home: Home;
  contentPages: ContentPage[];
  theme: Config["theme"];
}

export default function useInitApp() {
  const { setData } = useContext(dataStore);
  const { setHome } = useContext(homeStore);
  const { setContentPages } = useContext(contentPagesStore);
  const { config, setConfig } = useContext(configStore);
  const { session, setSession } = useContext(sessionStore);
  const { setErrorInfo } = useContext(errorStore);
  const { setFeatureFlags } = useContext(featureFlagsStore);
  const [status, setStatus] = useState<LoaderStatus>();
  const athenaAPIClient = useAthenaAPIClient();
  const selectApp = useSelectApp();

  // The following block of code, paired with the update to the
  // initAppAndRecentSyncRef at the end of the initApp function below, makes
  // the app initialization wait for a recent AthenaEvents sync with the
  // backend before completing. That means that if all other steps of the app
  // initialization process have completed, but the last AthenaEvents sync
  // occurred more than REQUIRED_SYNC_RECENCY milliseconds ago, the app waits
  // for another sync (which in the meantime has been kicked-off under the
  // hood) before completing initialization.
  //
  // This makes it so that the app always starts with a reasonably-recent set
  // of AthenaEvents in the local database.
  //
  // However, there are also scenarios in which a sync cannot be performed, so
  // we obviously don't want to wait for it, otherwise the app would get stuck
  // in waiting and wouldn't complete loading.
  //
  // These scenarios are:
  //
  // 1. We're in the specimen version of the app. This version doesn't collect
  //    and sync events altogether.
  // 2. The user is offline. Syncing requires the user to be online, and we
  //    want the app to work offline as well. (Granted, in this case we
  //    implicitly accept the possibility that the user has a non-recent set
  //    of AthenaEvents in the local database.)
  // 3. One or more events repeatedly fail pushing, with a retryable error. In
  //    this case AthenaEventStream keeps retrying the push, and doesn't
  //    consider a sync to have happened until the push succeeds. (This is a
  //    limitation of rxdb, used under the hood by AthenaEventStream, which we
  //    cannot work around.)
  //
  // In these cases:
  //
  // 1. We just don't wait for the sync.
  // 2. We just don't wait for the sync.
  // 3. Once the app finishes initializing, we kick off a timeout of
  //    MAX_SYNC_WAITING_TIME. If the sync didn't happen within that time
  //    limit, we give up on waiting for it.
  //
  // All these three cases are handled below, at the bottom of the initApp
  // function.
  const initAppAndRecentSyncRef = useRef({
    recentSync: false,
    initApp: false,
  });
  const endWaitRecentSyncRef = useRef<
    ((extraData?: LoadPhase["data"]) => void) | null
  >(null);
  const recentSync = useAthenaEventsRecentlySynced(REQUIRED_SYNC_RECENCY);
  useEffect(() => {
    initAppAndRecentSyncRef.current.recentSync = recentSync;
    if (recentSync && initAppAndRecentSyncRef.current.initApp) {
      endWaitRecentSyncRef.current?.({ outcome: "synced" });
      setStatus(LoaderStatus.Success);
    }
  }, [recentSync]);

  const axiosClientInitialized = useAxiosClientInit(status);

  useEffect(() => {
    if (status !== LoaderStatus.Success) return;
    clearStaleClientReloadGuard();
    reportAppLoaded({
      "user.type": session.userType,
      "app.version": session.version,
    });
  }, [status, session.userType, session.version]);

  useEventStreamEarlySubscribers();

  useInitServiceWorker();

  useExercisesQuery({
    enabled:
      status === LoaderStatus.Success &&
      localStorage.getItem(localStorage.Key.VERSION) !== undefined,
  });

  useEffect(() => {
    let dsn: string;
    if (window.location.hostname.endsWith("miaseconde.fr")) {
      dsn =
        "https://3cbf506f4ab14f56864bbe44f792c687@glitchtip.mia-prod.evidenceb-services.com/7";
    } else {
      dsn =
        "https://206002ca458cd96978c182ad8f0d14f7@sentry.evb-tools.evidenceb-services.com/12";
    }
    const environment = getEnvName(window.location.hostname);
    const release = resolveSentryRelease();
    console.log("SENTRY RELEASE : ", release);
    //SENTRY INIT

    if (environment !== "LOCAL")
      Sentry.init({
        dsn: dsn,
        integrations: [
          Sentry.breadcrumbsIntegration({
            console: true,
            dom: true,
            fetch: true,
            history: true,
          }),
          Sentry.browserTracingIntegration(),
        ],
        tracePropagationTargets: [
          "https://athena-auth",
          "https://athena-resources",
          "https://athena-users",
          "https://athena-resources",
          "https://*.evidenceb-services.com",
        ],
        tracesSampler: (samplingContext) => {
          // Force sampling perf events
          if (
            samplingContext.name === "app.startup" ||
            samplingContext.name.startsWith("ai.")
          )
            return 1;
          return isProductionEnvironment(window.location.hostname) ? 0.05 : 0.5;
        },
        environment,
        release: release + "@" + environment,
      });
  }, []);

  useSetSentryContext();

  useEffect(() => {
    if (isSpecimenVersion()) {
      (async function initSpecimen() {
        const variation = await getSpecimenVariation();
        athenaAPIClient.setSpecimenVariation(variation);
      })();
    }
  }, [athenaAPIClient]);

  useEffect(() => {
    const setError = (error: InitError) => {
      const showErrorPage = () => {
        setErrorInfo({
          displayModal: false,
          page: { code: error.code },
        });
        setStatus(LoaderStatus.Error);
      };
      if (!error.reloadWithFreshClient) {
        showErrorPage();
        return;
      }
      reloadOnceWithFreshClient().then((reloading) => {
        if (!reloading) showErrorPage();
      });
    };

    if (!axiosClientInitialized || typeof status !== "undefined") return;
    if (isSpecimenVersion() && !athenaAPIClient.specimenVariation) return;

    setStatus(LoaderStatus.Loading);

    const endInitAppPhase = startLoadPhase("init.app");

    (async function initApp() {
      const isSpecimen = isSpecimenVersion();
      let specimenVariation: string | false = false;
      if (isSpecimen) specimenVariation = await getSpecimenVariation();
      setConfig((curr) => ({
        ...curr,
        specimenVariation,
      }));

      // Token resolution
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const urlToken = urlParams.get("token") as string;
      const authService = urlParams.get("auth") as AuthService;
      const localStorageToken = localStorage.getItem<string>(
        localStorage.Key.TOKEN,
      );

      let token: string;
      let tokenPayload: TokenPayload;

      try {
        if (!isSpecimen) {
          token = tokenResolver(urlToken, localStorageToken);
          axiosClient.apiToken = token;
          localStorage.setItem(localStorage.Key.TOKEN, token);
        }
        tokenPayload = await trackLoadPhase("init.token-payload", () =>
          athenaAPIClient.getTokenPayload(),
        );

        // If undefined = user is not coming from Google Classroom
        // If true = user is coming from Google Classroom and is already logged in as the same user
        // If false = user is coming from Google Classroom but is not logged in as the same user
        if (isSameGoogleClassroomUser() === false) {
          localStorage.clear();
          throw new Error("Google user is not the same, resetting connection");
        }

        // If we come from Google Classroom we update the data
        // to make sure we have the correct data from the url params
        updateGoogleClassroomData();

        // [TEMP] hotfix to prevent access to /auth route when the user is logged
        // should be removed when the router as a whole is fixed
        if (`${window.location.pathname}/`.startsWith("/auth/")) {
          window.location.assign("/");
          return;
        }
      } catch (error) {
        // 1st iteration of the "no token authentication", it will be reworked & improved at a later date when we recode the entire useInitApp
        // Check if app exists in applications.json and if it has a "AUTH_REQUIRED" authMode
        try {
          const baseUrl = window.location.origin + (appConfig.basePath ?? "/");
          const { data } = await axios.get(`${baseUrl}json/applications.json`);
          const { currentApp, config: applicationConfig } = applicationResolver(
            data,
            getHost(),
            window.location.search,
          );
          const minimalConfigPath =
            currentApp.globalConfig ?? applicationConfig?.globalConfig;
          let redirects = false;
          // If url as google classroom params, store them in localStorage
          getGoogleClassroomData();

          if (
            authMatch(applicationConfig.authMode, [
              AuthMode.DirectAccess,
              AuthMode.Authentication,
            ]) &&
            minimalConfigPath
          ) {
            redirects = await redirectIfNotAuthPath(
              config.apiUrls.endpoints,
              minimalConfigPath,
              authService,
              currentApp,
            );
          }

          if (
            !redirects &&
            authMatch(applicationConfig.authMode, AuthMode.Authentication) &&
            minimalConfigPath
          ) {
            try {
              await selectApp(currentApp, applicationConfig);
              setStatus(LoaderStatus.Success);
              return;
            } catch (_err) {
              // Fail to load minimal_globalConfig.json
              // todo: rework error handling
              setError(INIT_ERRORS.token);
              Sentry.captureException({
                error: error,
                messsage: "ERROR: Fail to load minimal_globalConfig.json",
              });
              console.error("ERROR: Fail to load minimal_globalConfig.json");
              return;
            }
          } else {
            // app has no authMode = AUTH_REQUIRED
            // todo: rework error handling
            setError(INIT_ERRORS.token);
            console.log("Token not found in useInitApp");
            if (!currentApp.globalConfig || !applicationConfig.availableApps) {
              Sentry.captureException({
                error: error,
                messsage: "ERROR: no globalconfig path for app",
              });
              console.error("ERROR: no globalconfig path for app");
            } else {
              Sentry.captureException({
                error: error,
                messsage: "ERROR: app has no authMode = AUTH_REQUIRED",
              });
              console.error("ERROR: app has no authMode = AUTH_REQUIRED");
            }
            return;
          }
        } catch (err) {
          // Fail to load applications.json
          // todo: rework error handling
          setError(INIT_ERRORS.token);
          Sentry.captureException({
            error: err,
            messsage: "initApp() caught error",
          });
          console.error("initApp() caught error: ", err);
          return;
        }
      }

      let useHistoryFrom = StatementHistory.LRS;

      if (isSpecimen) {
        useHistoryFrom = StatementHistory.noHistory;
      }

      // Version resolution
      const urlVersion = urlParams.get("version") as string;
      const localStorageVersion = localStorage.getItem<string>(
        localStorage.Key.VERSION,
      );
      let version: string;
      try {
        version = versionResolver(
          tokenPayload,
          urlVersion,
          localStorageVersion,
        );
        localStorage.setItem(localStorage.Key.VERSION, version);
      } catch (_error) {
        version = ""; // If error, version variable needs to be assigned here so it can be a string type
        setError(INIT_ERRORS.version);
        return;
      }

      // Get user info
      const userType = tokenPayload.role.toUpperCase() as UserType;
      setSession((curr) => ({ ...curr, userType, version }));
      let user: Teacher | Student;
      try {
        user = await trackLoadPhase("init.get-user", () =>
          athenaAPIClient.getUser(tokenPayload),
        );
      } catch (_error) {
        setError(INIT_ERRORS.getUser);
        return;
      }
      let userToSessionData = userToSession(user, userType, isSpecimen);

      // Get duos if needed TODO : this could be async
      if (userType === UserType.Teacher) {
        const duos = await trackLoadPhase("init.get-duos", () =>
          athenaAPIClient.getDuos(user.id),
        );
        userToSessionData = {
          ...userToSessionData,
          duos: duos.map(userDuoToSessionDuo),
        };
      }

      // Get GlobalConfig
      let globalConfig: GlobalConfig;
      const request_fragments = effectiveFragments(user);
      try {
        globalConfig = await trackLoadPhase("init.global-config", () =>
          athenaAPIClient.getGlobalConfig(version, request_fragments),
        );
        handleServiceWorkerFlags(globalConfig);
        setConfig((config) => {
          return {
            ...config,
            lang: globalConfig.config.lang,
            disabledModules: globalConfig.config.disabledModules,
            hiddenModules: globalConfig.config.hiddenModules,
          };
        });
      } catch (_error) {
        setError(INIT_ERRORS.config);
        Sentry.captureException(_error);
        return;
      }
      const url = getLogoutRedirectUrl(globalConfig.config, user.provider);
      localStorage.setItem(localStorage.Key.LOGOUT_REDIRECT_URL, url);
      // Resolve authentication
      let displaySignIn: boolean;
      try {
        displaySignIn = await trackLoadPhase("init.should-sign-in", () =>
          shouldSignIn(
            user,
            globalConfig.config.auth.mode,
            tokenPayload.role.toUpperCase() as UserType,
            globalConfig.config.declinaison,
            athenaAPIClient.getClassroom.bind(athenaAPIClient),
          ),
        );
      } catch (_error) {
        //TODO error details ?
      }

      // TEMP
      const tempGlobalconfig: Omit<GlobalConfig, "config"> & {
        config: Config;
      } = {
        ...globalConfig,
        config: {
          ...globalConfig.config,
          ai: globalConfig.config.ai,
          mode: globalConfig.config.mode && {
            ...globalConfig.config.mode,
            test: normalizeExperimentationConfig(globalConfig.config.mode.test),
          },
        },
      };

      // Feature flags
      const mergedFlags = {
        ...tempGlobalconfig.config.features,
        ...tempGlobalconfig.config.releaseFlags,
        ...getLocalStorageFlags(),
      } as { [key: string]: any };
      mergedFlags.isSpecimen = isSpecimen;
      const flattenedFlags = flattenBooleansInObject(mergedFlags);
      setFeatureFlags(flattenedFlags);

      // Get data
      let data: Data;
      try {
        const rawData = await trackLoadPhase(
          "init.get-questions-no-content",
          () =>
            athenaAPIClient.getData(
              version,
              flattenedFlags["init-bandit-manchot-with-adaptive-test"],
              "questions-no-content",
            ),
        );
        data = await trackLoadPhase("init.process-data", async () =>
          removeNonVisible(
            rawData,
            globalConfig.config.disabledModules,
            globalConfig.config.hiddenModules,
          ),
        );
      } catch (_error) {
        setError(INIT_ERRORS.data);
        return;
      }

      // Register debug tools
      registerDebugUtils(data, athenaAPIClient);

      await trackLoadPhase("init.apply-config", async () => {
        setSession((session) => {
          return {
            ...session,
            ...userToSessionData,
            appVariation: globalConfig.config.declinaison,
            evidencebId: tokenPayload.sub,
            sessionId: uuid(),
            theme: globalConfig.theme.type ?? ThemeType.Light,
            flags: {
              ...session.flags,
              useHistoryFrom,
              displaySignIn: displaySignIn,
            },
            specimen: isSpecimen,
            school: user.school,
          };
        });

        setConfig((config) => merge({}, config, tempGlobalconfig.config));

        setHome(globalConfig.home);
        setContentPages(globalConfig.contentPages);

        const fontAssetsDetails: AssetsDetails = {
          version,
          contentUrl: config.apiUrls.endpoints.assetsProxy,
          folderpath: "interface/",
          basePath: appConfig.basePath,
        };

        applyThemeWithLegacy({ fontAssetsDetails, ...globalConfig.theme });
      });

      let mappedModules: Module[] | undefined;
      let mappedObjectives: Objective[] | undefined;

      const endResolveSubjectPhase = startLoadPhase("init.resolve-subject");
      if (flattenedFlags.requireMapping) {
        try {
          const mappingTree = await trackLoadPhase("init.mapping-tree", () =>
            athenaAPIClient.getMappingTree(),
          );

          if (mappingTree.id) {
            setConfig((config) => ({ ...config, mappingTree }));

            const learningEntries = getLearningEntries(tempGlobalconfig);

            if (learningEntries.length !== 0) {
              try {
                const learningSet: {
                  [key: string]: Objective;
                } = {};
                await trackLoadPhase(
                  "init.learning-sets",
                  () =>
                    Promise.all(
                      learningEntries.map(async (learningSetId) => {
                        const newLearningSet =
                          await athenaAPIClient.getLearningSet(
                            learningSetId!,
                            // Depth of objectives, in which lay mapping information
                            { depth: "3" },
                          );
                        Object.assign(learningSet, newLearningSet);
                      }),
                    ),
                  { count: learningEntries.length },
                );

                mappedModules = data.modules.map((mod) => ({
                  ...mod,
                  mapping_nodes: learningSet[mod.id]?.mapping_nodes,
                }));
                mappedObjectives = data.objectives.map((obj) => ({
                  ...obj,
                  mapping_nodes: learningSet[obj.id]?.mapping_nodes,
                }));
              } catch (_error) {
                setError(INIT_ERRORS.data);
                endResolveSubjectPhase();
                return;
              }
            }
          }

          const subjects = mappingTreeToSubjects(mappingTree);

          let activeSubject: string | undefined;

          if (userType === UserType.Teacher) {
            activeSubject = await getTeacherActiveSubject(
              user.id,
              userType,
              userToSessionData.extra ?? {},
              subjects,
              athenaAPIClient.updateUserExtra,
            );
          } else {
            activeSubject = getStudentActiveSubject(
              subjects,
              userToSessionData.extra ?? {},
            );
          }

          setSession((session) => ({
            ...session,
            extra: {
              ...session.extra,
              activeSubject: activeSubject,
            },
          }));
        } catch (_error) {
          setError(INIT_ERRORS.data);
          endResolveSubjectPhase();
          return;
        }
      } else {
        setSession((session) => ({
          ...session,
          extra: {
            ...session.extra,
            activeSubject: PLACEHOLDER_SUBJECT.id,
          },
        }));
      }
      endResolveSubjectPhase();

      //Experimentation mode
      const endExperimentationPhase = startLoadPhase("init.experimentation");
      const testMode = getExperimentationConfig(
        tempGlobalconfig.config.mode?.test,
        userType,
      );
      if (testMode) {
        const experimentationTestIds = [
          ...(flattenPool(testMode.pre) ?? []),
          ...(flattenPool(testMode.post) ?? []),
        ];
        if (experimentationTestIds.length > 0) {
          let experimentationTests: ExperimentationSet<Test>[];
          try {
            experimentationTests = await Promise.all<ExperimentationSet<Test>>(
              experimentationTestIds.map(async (experimentationTestId) => {
                const response = await athenaAPIClient.getLearningSet(
                  experimentationTestId,
                  {
                    depth: "3",
                    preview: "nested",
                  },
                );
                const learningSet = response as LearningSet;
                const learningSets = response?.learning_sets;

                let learningItems: Test[];
                if (learningSets) {
                  learningItems = (learningSet as any).learning_sets[0]
                    .learning_items;
                } else {
                  learningItems = [];
                }
                return {
                  ...learningSet,
                  author_id: "",
                  learning_set_type: "experimentation",
                  status: {
                    status: "sent",
                  },
                  learning_set_context: undefined,
                  learning_sets: undefined,
                  learning_items: learningItems,
                  learning_set_id: experimentationTestId,
                };
              }),
            );
          } catch (_error) {
            setError(INIT_ERRORS.experimentation);
            endExperimentationPhase();
            return;
          }
          setSession((session) => ({
            ...session,
            experimentationTests: experimentationTests,
          }));
        }
      }
      endExperimentationPhase();

      setData({
        modules: mappedModules ?? data.modules,
        objectives: mappedObjectives ?? data.objectives,
        activities: data.activities,
        exercises: data.exercises,
      });

      initAppAndRecentSyncRef.current.initApp = true;
      endInitAppPhase();
      if (
        isSpecimen ||
        initAppAndRecentSyncRef.current.recentSync ||
        !navigator.onLine
      ) {
        setStatus(LoaderStatus.Success);
      } else {
        endWaitRecentSyncRef.current = startLoadPhase("init.wait-recent-sync");
        setTimeout(() => {
          endWaitRecentSyncRef.current?.({ outcome: "timeout" });
          setStatus(LoaderStatus.Success);
        }, MAX_SYNC_WAITING_TIME);
      }
    })().catch((error: unknown) => {
      Sentry.captureException(error);
      setError(INIT_ERRORS.uncaught);
    });
  }, [
    axiosClientInitialized,
    selectApp,
    config.apiUrls,
    setConfig,
    setContentPages,
    setData,
    setHome,
    setSession,
    athenaAPIClient,
    setErrorInfo,
    status,
    setFeatureFlags,
    config,
  ]);

  return { status, setStatus };
}

export const getLearningEntries = (tempGlobalconfig: { config: Config }) => {
  const modulesLearningEntries =
    tempGlobalconfig.config.pages.find(
      (page) => page.type === Pagetype.MODULELIST,
    )?.entryLearningSetIds ?? [];

  if (
    modulesLearningEntries.length === 0 &&
    tempGlobalconfig.config.mode?.["soloai"]
  )
    return [tempGlobalconfig.config.mode?.["soloai"]];
  return modulesLearningEntries;
};

const getStudentActiveSubject = (subjects: Subject[], extra: UserExtra) => {
  if (
    extra.activeSubject &&
    subjects.some((subj) => subj.id === extra.activeSubject)
  )
    return extra.activeSubject;
  return subjects[0].id;
};

const getTeacherActiveSubject = async (
  userId: string,
  userType: UserType,
  extra: UserExtra,
  subjects: Subject[],
  updateActiveSubject: (
    extra: UserExtra,
    userId: string,
    userType: UserType,
  ) => Promise<User>,
): Promise<string | undefined> => {
  // If activeSubject is already set to an existing value
  if (
    extra.activeSubject &&
    subjects.some((subj) => subj.id === extra.activeSubject)
  )
    return extra.activeSubject;

  const matchingExtraSubject = subjects.find((subj) =>
    extra.subjects?.includes(subj.id),
  );
  // If there is a matching value in the subjects returned by the GAR
  if (matchingExtraSubject) {
    try {
      await updateActiveSubject(
        { activeSubject: matchingExtraSubject.id },
        userId,
        userType,
      );
      return matchingExtraSubject.id;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const handleServiceWorkerFlags = (config: GlobalConfig) => {
  const swFlags = SERVICE_WORKER_FLAGS.filter(
    (flag) =>
      config.config.releaseFlags?.[flag] ||
      config.config.features[flag as keyof GlobalConfig["config"]["features"]],
  );
  let flagAdded = false;
  try {
    swFlags.forEach((flag) => {
      if (!getLocalStorageFlags()[flag]) {
        setLocalStorageFlag(flag);
        flagAdded = true;
      }
    });
  } catch (_error) {
    // localStorage can throw (e.g. Firefox with strict tracking protection /
    // cookies blocked); don't let that break the whole login flow.
    Sentry.captureException(_error);
    return;
  }
  if (flagAdded) {
    window.location.reload();
  }
};
